import { useEffect, useState } from "react";
import { X, Copy, TwitterLogo, WhatsappLogo, LinkSimple, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { AvatarIcon, CountryFlag } from "@/lib/identity";

/**
 * ShareCardModal — dismissible modal that renders an "NK Card" for the
 * current viewer and offers social share buttons. Card is generated
 * client-side (HTML → CSS-styled DOM) and captured to an image via the
 * browser's Canvas / html-to-image on demand.
 *
 * Props:
 *   open       (bool)             — modal visibility
 *   onClose    (fn)               — close handler
 *   profile    (object)           — /season/profile/me response
 *   seasonNumber (number)         — current season number
 *   variant    ('season'|'always') — controls copy tone
 */
export default function ShareCardModal({ open, onClose, profile, seasonNumber, variant = "always" }) {
  const [downloading, setDownloading] = useState(false);
  if (!open || !profile) return null;
  const u = profile.user || {};
  const s = profile.stats || {};
  const rank = profile.season_rank;
  const handle = u.username || u.user_id;
  // Human-facing URL (SPA):
  const humanUrl = `${window.location.origin}/u/${handle}`;
  // Social-preview URL (backend OG meta HTML → high-quality PNG card):
  const shareUrl = `${window.location.origin}/api/share/u/${handle}`;
  const shareText = variant === "season"
    ? `I finished Season ${seasonNumber} as ${rank ? `#${rank}` : "an official"} player on NKognito 🏆 Africa's Knowledge Arena. Join me:`
    : `I'm playing NKognito — Africa's Knowledge Arena for real cash prizes. Check out my NK Card:`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(humanUrl);
      toast.success("Profile link copied");
    } catch {
      toast.error("Copy failed — grab it from the URL bar");
    }
  };

  const shareX = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const shareWa = () => {
    const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText + " " + shareUrl)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const downloadCard = async () => {
    // Lazy-load html-to-image so the base bundle stays small
    setDownloading(true);
    try {
      const { toPng } = await import("html-to-image");
      const node = document.getElementById("nk-share-card");
      if (!node) throw new Error("card missing");
      const dataUrl = await toPng(node, {
        pixelRatio: 2, cacheBust: true,
        backgroundColor: "#0d1210",
      });
      const link = document.createElement("a");
      link.download = `nkognito-${u.username || u.user_id}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Card downloaded");
    } catch (e) {
      toast.error("Couldn't render card — try Copy Link instead");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="share-card-modal"
    >
      <div
        className="bg-nk-bg border border-nk-line rounded-md w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-nk-line">
          <div>
            <p className="eyebrow text-nk-gold">Share your NK Card</p>
            <p className="text-xs text-nk-mute mt-0.5">Your permanent shareable profile</p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="share-card-modal-close"
            className="text-nk-mute hover:text-nk-text"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* NK CARD (rendered visually + captured to image) */}
        <div className="p-6 bg-gradient-to-br from-nk-bg to-black/60">
          <div
            id="nk-share-card"
            className="rounded-lg border-2 border-nk-gold/40 p-6 bg-gradient-to-br from-nk-surface via-nk-bg to-black relative overflow-hidden"
            style={{ aspectRatio: "1200 / 630" }}
          >
            {/* corner marker */}
            <div className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.24em] text-nk-gold/70 font-mono">
              NKognito · Season {seasonNumber || 1}
            </div>
            <div className="flex items-start gap-4 h-full">
              <AvatarIcon id={u.avatar} size={64} />
              <div className="flex-1 min-w-0">
                <h2 className="font-display text-2xl font-medium text-white tracking-tight truncate">
                  {u.username || "Player"}
                </h2>
                <div className="flex items-center gap-2 mt-1">
                  <CountryFlag country={u.country} size={14} />
                  <span className="text-sm text-nk-mute">
                    Playing for {u.country === "GH" ? "Ghana" : u.country === "NG" ? "Nigeria" : "the world"}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-4 mt-5">
                  <Stat label="Season score" value={(s.season_score || 0).toLocaleString()} accent />
                  <Stat label="Rank" value={rank ? `#${rank}` : "—"} />
                  <Stat label="Rating" value={s.knowledge_rating || 1000} />
                </div>
                <p className="text-[10px] uppercase tracking-widest text-nk-mute mt-6 font-mono">
                  Africa's Competitive Knowledge Arena · nkognitogames.com
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Share row */}
        <div className="p-4 border-t border-nk-line grid grid-cols-2 sm:grid-cols-4 gap-2">
          <ShareBtn onClick={copyLink} label="Copy link" testid="share-copy" Icon={LinkSimple} />
          <ShareBtn onClick={shareX} label="Share on X" testid="share-x" Icon={TwitterLogo} />
          <ShareBtn onClick={shareWa} label="WhatsApp" testid="share-wa" Icon={WhatsappLogo} />
          <ShareBtn
            onClick={downloadCard}
            label={downloading ? "Rendering…" : "Download PNG"}
            testid="share-download"
            Icon={DownloadSimple}
            disabled={downloading}
          />
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent = false }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-[0.18em] text-nk-mute font-mono mb-1">{label}</p>
      <p className={`font-mono text-xl font-medium ${accent ? "text-nk-gold" : "text-white"}`}>{value}</p>
    </div>
  );
}

function ShareBtn({ onClick, label, testid, Icon, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className="flex items-center justify-center gap-1.5 px-3 py-2 border border-nk-line rounded-sm text-xs text-nk-text hover:border-nk-gold/60 hover:text-nk-gold transition-colors disabled:opacity-50"
    >
      <Icon size={14} weight="bold" />
      <span className="truncate">{label}</span>
    </button>
  );
}
